import React, { useState, useMemo } from 'react';
import { RefreshCw, File } from 'lucide-react';
import FileUpload from '../components/common/FileUpload';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import TaskProgressPanel from '../components/conversion/TaskProgressPanel';
import ConversionHistory from '../components/conversion/ConversionHistory';
import { useConversionProcess } from '../hooks/useConversionProcess';
import './Convert.css';

const EXTENSION_GROUPS = {
    pdf: ['pdf'],
    word: ['doc', 'docx'],
    excel: ['xls', 'xlsx', 'csv'],
    ppt: ['ppt', 'pptx'],
    image: ['jpg', 'jpeg', 'png', 'gif', 'bmp'],
    html: ['html', 'htm'],
};

const getFileType = (filename: string): string => {
    const ext = filename.split('.').pop()?.toLowerCase() || '';
    for (const [type, exts] of Object.entries(EXTENSION_GROUPS)) {
        if (exts.includes(ext)) {
            return type;
        }
    }
    return 'unknown';
};

const getSupportedTargets = (sourceType: string): { value: string, label: string }[] => {
    if (sourceType === 'pdf') {
        return [
            { value: 'word', label: 'Word (DOCX)' },
            { value: 'excel', label: 'Excel (XLSX)' },
            { value: 'ppt', label: 'PowerPoint (PPTX)' },
            { value: 'image', label: '图片 (Images)' },
            { value: 'html', label: 'HTML网页' },
        ];
    } else if (['word', 'excel', 'ppt', 'image', 'html'].includes(sourceType)) {
        return [{ value: 'pdf', label: 'PDF文档' }];
    }
    return [];
};

const getOperationName = (sourceType: string, targetType: string): string | null => {
    if (sourceType === 'pdf') {
        switch (targetType) {
            case 'word': return 'pdf_to_word';
            case 'excel': return 'pdf_to_excel';
            case 'ppt': return 'pdf_to_ppt';
            case 'image': return 'pdf_to_image';
            case 'html': return 'pdf_to_html';
        }
    } else if (targetType === 'pdf') {
        switch (sourceType) {
            case 'word': return 'word_to_pdf';
            case 'excel': return 'excel_to_pdf';
            case 'ppt': return 'ppt_to_pdf';
            case 'image': return 'image_to_pdf';
            case 'html': return 'html_to_pdf';
        }
    }
    return null;
};

const Convert: React.FC = () => {
    const [files, setFiles] = useState<File[]>([]);
    const [targetFormat, setTargetFormat] = useState<string>('');
    const [refreshKey, setRefreshKey] = useState(0);

    const {
        taskId,
        isCreating,
        processError,
        setProcessError,
        executeTask,
        resetTask,
    } = useConversionProcess();

    const sourceType = useMemo(() => {
        if (files.length === 0) return null;
        return getFileType(files[0].name);
    }, [files]);

    const targetOptions = useMemo(() => {
        if (!sourceType) return [];
        return getSupportedTargets(sourceType);
    }, [sourceType]);

    // Update target format if only one option is available or if currently selected is invalid
    React.useEffect(() => {
        if (targetOptions.length > 0) {
            if (!targetFormat || !targetOptions.find(o => o.value === targetFormat)) {
                setTargetFormat(targetOptions[0].value);
            }
        }
    }, [targetOptions, targetFormat]);

    const handleFilesSelected = (selectedFiles: File[]) => {
        const type = getFileType(selectedFiles[0].name);
        if (type === 'unknown') {
            setProcessError('不支持的文件类型。支持：PDF, Word, Excel, PPT, 图片, HTML。');
            return;
        }

        // Single file mode for now, to ensure one operation type applies
        setFiles([selectedFiles[0]]);
        setProcessError(null);
    };

    const handleConvert = async () => {
        if (files.length === 0) {
            setProcessError('请选择一个文件');
            return;
        }

        if (!sourceType || !targetFormat) {
            setProcessError('请选择目标格式');
            return;
        }

        const operation = getOperationName(sourceType, targetFormat);
        if (!operation) {
            setProcessError('不支持该转换类型');
            return;
        }

        await executeTask(files[0], operation);
    };

    const handleReset = () => {
        resetTask();
        setFiles([]);
        setTargetFormat('');
    };

    return (
        <div className="convert-page">
            <div className="container">
                <div className="page-header">
                    <h1>文件格式转换</h1>
                    <p>支持 PDF 与常见办公文档、图片格式的相互转换</p>
                </div>

                <div className="page-content">
                    <Card glass>
                        {!taskId ? (
                            <>
                                <div className="upload-section">
                                    <FileUpload
                                        onFilesSelected={handleFilesSelected}
                                        multiple={false}
                                        maxFiles={1}
                                        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.html,.htm"
                                    />
                                </div>

                                {files.length > 0 && sourceType && (
                                    <>
                                        <div className="conversion-config">
                                            <div className="file-info-box">
                                                <File size={24} className="file-icon" />
                                                <div className="file-details">
                                                    <div className="file-name" title={files[0].name}>{files[0].name}</div>
                                                    <div className="file-size">{(files[0].size / 1024 / 1024).toFixed(2)} MB</div>
                                                </div>
                                            </div>

                                            <div className="target-select-section">
                                                <label className="target-label">转换为</label>
                                                <div className="target-options">
                                                    {targetOptions.map((option) => (
                                                        <label
                                                            key={option.value}
                                                            className={`target-option ${targetFormat === option.value ? 'selected' : ''}`}
                                                        >
                                                            <input
                                                                type="radio"
                                                                name="targetFormat"
                                                                value={option.value}
                                                                checked={targetFormat === option.value}
                                                                onChange={() => setTargetFormat(option.value)}
                                                                disabled={isCreating}
                                                            />
                                                            <div className="option-content">
                                                                <strong>{option.label}</strong>
                                                            </div>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="action-section">
                                            <Button
                                                variant="primary"
                                                size="lg"
                                                onClick={handleConvert}
                                                isLoading={isCreating}
                                                icon={<RefreshCw />}
                                                disabled={isCreating || !targetFormat}
                                            >
                                                {isCreating ? '正在准备转换...' : '开始转换'}
                                            </Button>
                                        </div>
                                    </>
                                )}

                                {processError && (
                                    <div className="message message-error" style={{ marginTop: '15px' }}>
                                        {processError}
                                    </div>
                                )}
                            </>
                        ) : (
                            <TaskProgressPanel taskId={taskId} onReset={handleReset} onComplete={() => setRefreshKey(prev => prev + 1)} />
                        )}
                    </Card>

                    <div className="tips-section">
                        <Card>
                            <h3>💡 使用提示</h3>
                            <ul>
                                <li>支持以下格式互转：PDF 与 Word, Excel, PPT, 图片, HTML。</li>
                                <li>上传文件后会自动识别其格式，并显示可转换的目标格式。</li>
                                <li>云端转换快速且不额外耗费您的电脑性能。</li>
                                <li>转换后的文件会自动开始下载，并且如果下载未弹出，可以手动点击对应任务界面中的下载按钮。</li>
                            </ul>
                        </Card>
                    </div>

                    <ConversionHistory operationFilter={['pdf_to_word', 'pdf_to_excel', 'pdf_to_ppt', 'pdf_to_image', 'pdf_to_html', 'word_to_pdf', 'excel_to_pdf', 'ppt_to_pdf', 'image_to_pdf', 'html_to_pdf']} onRefreshTrigger={refreshKey} />
                </div>
            </div>
        </div>
    );
};

export default Convert;
